import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import Btn from "./Btn";
import StatFlipCards from "./StatFlipCards";
import StatFlipGrid from "./StatFlipGrid";
import StatList from "./StatList";

/** Which "By the numbers" treatment to show beside the copy. "none" = plain numbers in the
 *  site's editorial style (Steve's pick, Oct 25). "four" / "one" are staging previews for
 *  marketing: condensed copy with four flip cards, or one card flipping through the stats. */
export type StatsVariant = "none" | "four" | "one";

/** "Who we are" — purpose statement + the three uniques, then one button per audience page. */
const PurposeSection = ({ stats = "none" }: { stats?: StatsVariant }) => (
  <section className="bg-background py-20 md:py-24">
    <div className="mx-auto max-w-[1200px] px-6">
      <SectionLabel>Our Purpose</SectionLabel>
      <Reveal>
        <div className="mt-8 grid grid-cols-1 items-center gap-10 lg:grid-cols-[1.35fr_1fr]">
          <div>
            <h2 className="max-w-4xl text-4xl leading-tight md:text-6xl">
              Love God. <span className="text-primary">Love People.</span>
            </h2>
            {stats !== "none" ? (
              /* DRAFT condensed copy (marketing to rework) — only with the card previews */
              <div className="font-body mt-8 max-w-2xl space-y-5 text-base leading-relaxed text-foreground/85 md:text-lg">
                <p>
                  Since 1999, HXP has taken teens ages 14–19, and the parents and leaders
                  who serve alongside them, on two-week humanitarian service trips around
                  the world. Phones off. Real, sweaty work on projects that outlast you.
                  Friends you keep forever. We promise the{" "}
                  <span className="accent-hand text-primary">
                    hardest and best summer of your life,
                  </span>{" "}
                  because{" "}
                  <span className="accent-hand text-primary">there will be miracles.™</span>
                </p>
              </div>
            ) : (
            <div className="font-body mt-8 max-w-3xl space-y-5 text-base leading-relaxed text-foreground/85 md:text-lg">
              <p>
                HXP takes teens ages 14–19 — and the parents, grandparents, and leaders
                who serve alongside them — on two-week humanitarian expeditions around
                the world. Every trip is carried by a world-class team — full-time
                staff who spend the year vetting every project, and Trip Leaders chosen
                through one of the most selective processes anywhere. And we make you a
                promise: the{" "}
                <span className="accent-hand text-primary">
                  hardest and best summer of your life.
                </span>{" "}
                Phones off. Real, sweaty work on projects that outlast you. And the most
                positive peer group you've ever been part of — the kind of friends you
                keep forever. Since 1999, more than 48,000 Builders have served in 40
                countries and come home changed, because{" "}
                <span className="accent-hand text-primary">there will be miracles.™</span>
              </p>
            </div>
            )}
          </div>
          {stats === "four" ? (
            <StatFlipGrid />
          ) : stats === "one" ? (
            <StatFlipCards />
          ) : (
            <StatList />
          )}
        </div>
      </Reveal>
      <Reveal>
        {/* Phones: one full-width button per row (no odd one out). sm+: a wrapping row. */}
        <div className="mt-12 flex flex-col gap-3 sm:flex-row sm:flex-wrap md:gap-4">
          <Btn href="/youth-builders/" className="w-full sm:w-auto">
            Be a Youth Builder
          </Btn>
          <Btn href="/parent-builders/" variant="dark" className="w-full sm:w-auto">
            Be a Parent Builder
          </Btn>
          <Btn href="/trip-leaders/" variant="green" className="w-full sm:w-auto">
            Be a Trip Leader
          </Btn>
          <Btn href="/about/" variant="outline" className="w-full sm:w-auto">
            Our Story
          </Btn>
        </div>
      </Reveal>
    </div>
  </section>
);

export default PurposeSection;
